/**
 * State for the self-funding paywall step: the consumer's access, the Checkout Session whose amount the
 * offer shows (created only for a consumer who can buy: never paid, or expired), and where buying
 * leads (the checkout step, carrying any `returnTo`). Consumers with access, and advisors or wizards
 * viewing, continue into the sequence at the step last reached, or, having chosen to self-fund from
 * the options, on to that choice; no session is created for them.
 */
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@shared';
import { useIsAgeIneligibleForQuotes } from '@/features/client-onboarding/hooks/use-is-age-ineligible-for-quotes';
import { resolveSelfFundResumeStep } from '../lib/self-fund-access';
import { useConsumerFlow } from './use-consumer-flow';
import { useConsumerPlan } from './use-consumer-plan';
import { useSelfFundAccess } from './use-self-fund-access';
import { afterPurchaseFor, useSelfFundCheckoutSession } from './use-self-fund-checkout-session';
import { useSelfFundChoicePath } from './use-self-fund-choice-path';

export function useSelfFundPaywall(clientId: string) {
  const { goTo, finish, returnTo } = useConsumerFlow(clientId);
  const navigate = useNavigate();
  const selfFundChoicePath = useSelfFundChoicePath(clientId);
  const { access, endsAt, isConsumerSession } = useSelfFundAccess();
  const { plan } = useConsumerPlan(clientId);
  const isAgeIneligible = useIsAgeIneligibleForQuotes();
  const canBuy = isConsumerSession && access !== 'active';
  const checkout = useSelfFundCheckoutSession(clientId, {
    enabled: canBuy,
    afterPurchase: afterPurchaseFor(returnTo),
  });

  return {
    access,
    endsAt,
    canBuy,
    isAgeIneligible,
    price:
      checkout.amountInCents === null ? null : formatCurrency(checkout.amountInCents / 100, 0, 2),
    isPriceLoading: checkout.isPending,
    startCheckout: () => goTo('self-fund-checkout', { returnTo: returnTo ?? undefined }),
    // Active consumers (and advisors viewing) carry on into the sequence where they left off, or on
    // to the self-funding choice made from the options.
    continuePlan: () =>
      returnTo === 'options'
        ? navigate(selfFundChoicePath)
        : goTo(resolveSelfFundResumeStep(plan?.lastStep)),
    back: () => finish('options'),
  };
}
